/** The order header's immediate actions (plan §3, "Order page: the Actions menu for orders
 *  where the backend supports it") — what the backend offers for one order today:
 *
 *  - **Communication** — Emit event… (`console_events_emit`): the Orders grid's
 *    `EmitEventDialog` (`order.*` events) over a one-order selection.
 *  - **Diagnose** — AI diagnose (`console_entity_ai`): the Diagnose tab.
 *
 *  Each item is present only with its permission. `host` renders the open dialog. */
import { useCallback, useState, type ReactNode } from 'react';

import type { RecordActionGroup, RecordActionItem } from '@/features/records/page/RecordActionsMenu';
import { EmitEventDialog } from '@/features/emit-events/EmitEventDialog';
import { singleRecordBatchContext } from '@/features/records/actions/singleRecordBatch';
import { usePermissions } from '@/hooks/usePermissions';

export function useOrderImmediateActions({
  orderId,
  tenantId,
  onDiagnose,
}: {
  orderId: number;
  tenantId: number | null | undefined;
  onDiagnose: () => void;
}): { groups: RecordActionGroup[]; host: ReactNode } {
  const { hasPerm } = usePermissions();
  const [emitOpen, setEmitOpen] = useState(false);
  const close = useCallback(() => setEmitOpen(false), []);

  const communication: RecordActionItem[] = hasPerm('console_events_emit')
    ? [{ key: 'emit_event', label: 'Emit event…', onSelect: () => setEmitOpen(true) }]
    : [];
  const diagnose: RecordActionItem[] = hasPerm('console_entity_ai')
    ? [{ key: 'ai_diagnose', label: 'AI diagnose', onSelect: onDiagnose }]
    : [];

  return {
    groups: [
      { key: 'communication', label: 'Communication', items: communication },
      { key: 'diagnose', label: 'Diagnose', items: diagnose },
    ],
    host: emitOpen ? (
      <EmitEventDialog ctx={singleRecordBatchContext({ id: orderId, tenantId, close })} grid="orders" />
    ) : null,
  };
}
